import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

const partners = [
  { name: 'TechVentures', logo: 'TV' },
  { name: 'BlockFund', logo: 'BF' },
  { name: 'CryptoCapital', logo: 'CC' },
  { name: 'DeFi Alliance', logo: 'DA' },
  { name: 'Web3 Labs', logo: 'W3' },
  { name: 'ChainPartners', logo: 'CP' },
];

export const Partners = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="partners"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#050505]' : 'bg-[#F8FAFC]'}`}
      ref={ref}
      data-testid="partners-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            Partners & Backers
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Trusted by <span className="gradient-text">Industry Leaders</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Backed by world-class investors and partners who believe in our vision.
          </p>
        </motion.div>

        {/* Partners Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8"
        >
          {partners.map((partner, index) => (
            <motion.div
              key={partner.name}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3, delay: 0.1 * index }}
              whileHover={{ scale: 1.05 }}
              className={`flex flex-col items-center justify-center p-8 rounded-2xl border group cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                isDark 
                  ? 'bg-white/[0.03] border-white/10' 
                  : 'bg-white border-black/5'
              }`}
            >
              {/* Logo placeholder */}
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-all">
                <span className="text-xl font-bold text-blue-400">{partner.logo}</span>
              </div>
              <span className={`text-sm font-medium transition-colors group-hover:text-blue-400 ${
                isDark ? 'text-zinc-400' : 'text-slate-600'
              }`}>
                {partner.name}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Partner CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className={`mb-4 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Interested in partnering with Polk Chain?
          </p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`px-8 py-3 rounded-full font-semibold transition-all border ${
              isDark 
                ? 'bg-white/10 hover:bg-white/20 text-white border-white/20' 
                : 'bg-black/5 hover:bg-black/10 text-gray-900 border-black/10'
            }`}
            data-testid="become-partner-btn"
          >
            Become a Partner
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};
